import {
  ProjectsCell,
  ReviewCell,
  ClientMenuCell,
  BrokersCell,
  ManagersCell,
  AdditionalGuestCell,
  ClientEventRow,
  INVITATION_STATUSES,
  PRESENCE_STATUSES,
  RECONFIRMATION_STATUSES,
  FioCell,
} from '@entities/clients';
import { DataTableColumn } from '@shared/ui/data-table';
import { Tag } from '@shared/ui/tag';
import { CallsFilterDropdown } from '../CallsFilterDropdown';
import {
  FILTER_FIELD_PRESENCE,
  FILTER_FIELD_INVITATION,
  FILTER_FIELD_PROJECTS,
  FILTER_FIELD_BROKER,
  FILTER_FIELD_MANAGER,
  FILTER_FIELD_CALLS,
  CALLS_FILTER_ALL,
  PRESENCE_FILTER_ALL,
  FILTER_FIELD_RECONFIRMATION_STATUS,
  RECONFIRMATION_STATUS_FILTER_ITEMS,
} from '../consts';
import { BrokerFilterDropdown } from '../BrokerFilterDropdown';
import { FilterDropdown } from '../FilterDropdown';
import { InvitationFilterDropdown } from '../InvitationFilterDropdown';
import { ManagerFilterDropdown } from '../ManagerFilterDropdown';
import { PresenceFilterDropdown } from '../PresenceFilterDropdown';
import styles from '../EventClientsTable.module.scss';
import { EventClientsTableColumnsParams } from './types';
import { FireSolidIcon, NoSymbolIcon } from '@shared/assets/icons';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import clsx from 'clsx';
import { Tooltip } from '@shared/ui/tooltip';

export const buildEventClientsTableColumns = ({
  dateStart,
  openFilterField,
  setOpenFilterField,
  serverFilters: {
    selectedProjectsFilter,
    selectedInvitationStatuses,
    selectedReconfirmationStatuses,
    selectedPresenceFilter,
  },
  clientFilters,
  filterItems,
  filterHandlers: {
    onApplyProjectsFilter,
    onApplyInvitationFilter,
    onApplyReconfirmationStatusFilter,
    onSelectPresenceFilter,
    onApplyBrokersFilter,
    onApplyManagerFilter,
    onSelectCallsFilter,
  },
  cellHandlers: {
    onChangeInvitation,
    onChangeReconfirmationStatus,
    onChangeReview,
    onChangeComment,
    onChangeInvitingManager,
    onDeleteClient,
    onChangeAdditionalGuest,
    onChangeMobileComment,
    onChangeSpecial,
  },
}: EventClientsTableColumnsParams): DataTableColumn<ClientEventRow>[] => {
  const hasProjectsFilter = selectedProjectsFilter.length > 0;
  const hasInvitationFilter =
    selectedInvitationStatuses.length > 0 && selectedInvitationStatuses.length !== INVITATION_STATUSES.length;
  const hasReconfirmationStatusesFilter =
    selectedReconfirmationStatuses.length > 0 &&
    selectedReconfirmationStatuses.length !== RECONFIRMATION_STATUSES.length;
  const hasPresenceFilter = selectedPresenceFilter !== PRESENCE_FILTER_ALL;
  const hasBrokerFilter = clientFilters.selectedBrokersFilter.length > 0;
  const hasManagerFilter = clientFilters.isInvitingManagersFilter
    ? clientFilters.selectedInvitingManagersFilter.length > 0
    : clientFilters.selectedAllManagersFilter.length > 0;
  const hasCallsFilter = clientFilters.selectedCallsFilter !== CALLS_FILTER_ALL;

  const canEditReconfirmationStatuses = (invitation: string) => {
    if (!dateStart) return false;

    const targetDate = new Date(dateStart);
    const now = new Date();

    const differenceMs = targetDate.getTime() - now.getTime();
    const differenceDays = Math.ceil(differenceMs / (1000 * 60 * 60 * 24));

    return invitation === 'CONFIRMED' && differenceDays <= 3;
  };

  return [
    {
      field: 'isSpecial',
      headerName: '',
      width: 32,
      renderHeader: () => <FireSolidIcon className={styles.specialIcon} />,
      renderCell: ({ isSpecial, ticketId }) => (
        <Tooltip className={styles.specialTooltip} position="topLeft" title="Особое выделение">
          <UnstyledButton
            className={clsx(styles.specialButton, isSpecial && styles.special)}
            onClick={() => onChangeSpecial(ticketId, !isSpecial)}
          >
            <FireSolidIcon />
          </UnstyledButton>
        </Tooltip>
      ),
    },
    {
      field: 'projects',
      headerName: 'Проекты',
      minWidth: 126,
      grow: 1.26,
      cellClassName: styles.cellText,
      renderCell: (row) => <ProjectsCell projects={row.projects} namesOnly />,
      sortable: true,
      showFilterIcon: true,
      isFilterActive: hasProjectsFilter,
      onFilterClick: () => {
        setOpenFilterField((prevField) => (prevField === FILTER_FIELD_PROJECTS ? null : FILTER_FIELD_PROJECTS));
      },
      isFilterOpen: openFilterField === FILTER_FIELD_PROJECTS,
      filterDropdown: (
        <FilterDropdown
          selectedProjects={selectedProjectsFilter}
          onApply={onApplyProjectsFilter}
          onClose={() => setOpenFilterField(null)}
        />
      ),
    },
    {
      field: 'clientInfo',
      headerName: 'ФИО',
      minWidth: 148,
      grow: 1.47,
      cellClassName: styles.fioCell,
      renderCell: ({ clientInfo, calls, ticketId }) => (
        <FioCell
          clientInfo={clientInfo}
          calls={calls}
          onChangeMobileComment={(value) => onChangeMobileComment(ticketId, value)}
        />
      ),
      sortable: true,
    },
    {
      field: 'salesManager',
      headerName: 'Менеджеры',
      minWidth: 148,
      grow: 1.47,
      cellClassName: styles.cellText,
      sortable: true,
      showFilterIcon: true,
      isFilterActive: hasManagerFilter,
      onFilterClick: () => {
        setOpenFilterField((prevField) => (prevField === FILTER_FIELD_MANAGER ? null : FILTER_FIELD_MANAGER));
      },
      isFilterOpen: openFilterField === FILTER_FIELD_MANAGER,
      filterDropdown: (
        <ManagerFilterDropdown
          isInvitingOnly={clientFilters.isInvitingManagersFilter}
          selectedAllManagers={clientFilters.selectedAllManagersFilter}
          selectedInvitingManagers={clientFilters.selectedInvitingManagersFilter}
          allManagerItems={filterItems.allManagerFilterItems}
          invitingManagerItems={filterItems.invitingManagerFilterItems}
          onApply={onApplyManagerFilter}
          onClose={() => setOpenFilterField(null)}
        />
      ),
      renderCell: (row: ClientEventRow) => (
        <ManagersCell
          salesManager={row.salesManager}
          invitingManager={row.invitingManager}
          onChangeInvitingManager={(value) => onChangeInvitingManager(row.ticketId, value)}
        />
      ),
    },
    {
      field: 'brokers',
      headerName: 'Брокер',
      minWidth: 148,
      grow: 1.47,
      cellClassName: styles.cellText,
      sortable: true,
      showFilterIcon: true,
      isFilterActive: hasBrokerFilter,
      onFilterClick: () => {
        setOpenFilterField((prevField) => (prevField === FILTER_FIELD_BROKER ? null : FILTER_FIELD_BROKER));
      },
      isFilterOpen: openFilterField === FILTER_FIELD_BROKER,
      filterDropdown: (
        <BrokerFilterDropdown
          items={filterItems.brokerFilterItems}
          selectedBrokers={clientFilters.selectedBrokersFilter}
          onApply={onApplyBrokersFilter}
          onClose={() => setOpenFilterField(null)}
        />
      ),
      renderCell: (row: ClientEventRow) => <BrokersCell brokers={row.brokers} />,
    },
    {
      field: 'calls',
      headerName: 'Отказ от звонков',
      minWidth: 140,
      grow: 1.37,
      cellClassName: styles.cellText,
      sortable: true,
      showFilterIcon: true,
      isFilterActive: hasCallsFilter,
      onFilterClick: () => {
        setOpenFilterField((prevField) => (prevField === FILTER_FIELD_CALLS ? null : FILTER_FIELD_CALLS));
      },
      isFilterOpen: openFilterField === FILTER_FIELD_CALLS,
      filterDropdown: (
        <CallsFilterDropdown
          selectedValue={clientFilters.selectedCallsFilter}
          onSelect={onSelectCallsFilter}
          onClose={() => setOpenFilterField(null)}
        />
      ),
      renderCell: ({ calls }) =>
        calls && (
          <span className={styles.callBan}>
            Запрет звонков <NoSymbolIcon color="currentColor" />
          </span>
        ),
    },
    {
      field: 'invitation',
      headerName: 'Статус приглашения',
      minWidth: 198,
      grow: 1.96,
      cellClassName: styles.cellText,
      sortable: true,
      showFilterIcon: true,
      isFilterActive: hasInvitationFilter,
      onFilterClick: () => {
        setOpenFilterField((prevField) => (prevField === FILTER_FIELD_INVITATION ? null : FILTER_FIELD_INVITATION));
      },
      isFilterOpen: openFilterField === FILTER_FIELD_INVITATION,
      filterDropdown: (
        <InvitationFilterDropdown
          selectedStatuses={selectedInvitationStatuses}
          onApply={onApplyInvitationFilter}
          onClose={() => setOpenFilterField(null)}
        />
      ),
      renderCell: (row) => (
        <Tag
          items={INVITATION_STATUSES}
          onChange={(value) => onChangeInvitation(row.ticketId, value)}
          selectedValue={row.invitation}
        />
      ),
    },
    {
      field: 'reconfirmationStatus',
      headerName: 'Повторное подтверждение',
      minWidth: 208,
      grow: 2.05,
      cellClassName: styles.cellText,
      sortable: true,
      showFilterIcon: true,
      isFilterActive: hasReconfirmationStatusesFilter,
      onFilterClick: () => {
        setOpenFilterField((prevField) =>
          prevField === FILTER_FIELD_RECONFIRMATION_STATUS ? null : FILTER_FIELD_RECONFIRMATION_STATUS
        );
      },
      isFilterOpen: openFilterField === FILTER_FIELD_RECONFIRMATION_STATUS,
      filterDropdown: (
        <InvitationFilterDropdown
          selectedStatuses={selectedReconfirmationStatuses}
          onApply={onApplyReconfirmationStatusFilter}
          onClose={() => setOpenFilterField(null)}
          options={RECONFIRMATION_STATUS_FILTER_ITEMS}
          filterField={FILTER_FIELD_RECONFIRMATION_STATUS}
        />
      ),
      renderCell: ({ reconfirmationStatus, ticketId, invitation }) => (
        <Tag
          items={RECONFIRMATION_STATUSES}
          onChange={(value) => onChangeReconfirmationStatus(ticketId, value)}
          selectedValue={reconfirmationStatus}
          isDisabled={!canEditReconfirmationStatuses(invitation)}
        />
      ),
    },
    {
      field: 'additionalGuest',
      headerName: 'Гость +1',
      minWidth: 148,
      grow: 1.47,
      cellClassName: styles.cellText,
      sortable: true,
      renderCell: ({ clientInfo, ticketId, additionalGuest }) => (
        <AdditionalGuestCell
          additionalGuest={additionalGuest}
          clientInfo={clientInfo}
          onChangeAdditionalGuest={(value) => onChangeAdditionalGuest(ticketId, value)}
        />
      ),
    },
    {
      field: 'checked',
      headerName: 'Явка',
      minWidth: 102,
      grow: 1,
      cellClassName: styles.cellText,
      sortable: true,
      showFilterIcon: true,
      isFilterActive: hasPresenceFilter,
      onFilterClick: () => {
        setOpenFilterField((prevField) => (prevField === FILTER_FIELD_PRESENCE ? null : FILTER_FIELD_PRESENCE));
      },
      isFilterOpen: openFilterField === FILTER_FIELD_PRESENCE,
      filterDropdown: (
        <PresenceFilterDropdown
          selectedValue={selectedPresenceFilter}
          onSelect={onSelectPresenceFilter}
          onClose={() => setOpenFilterField(null)}
        />
      ),
      renderCell: (row: ClientEventRow) => (
        <Tag items={PRESENCE_STATUSES} selectedValue={row.checked?.toString()} isDisabled />
      ),
    },
    {
      field: 'comment',
      headerName: 'Примечание',
      minWidth: 148,
      grow: 1.47,
      cellClassName: styles.cellText,
      renderCell: (row) => (
        <ReviewCell
          review={row.comment}
          onChange={(value) => onChangeComment(row.ticketId, value)}
          participantName={row.clientInfo.FIO}
          emptyText="Нет информации"
          placeholder="Введите текст"
          modalTitle="Примечание к участию"
          fieldLabel="Примечание к участию"
        />
      ),
    },
    {
      field: 'review',
      headerName: 'Отзыв клиента',
      minWidth: 148,
      grow: 1.47,
      cellClassName: styles.cellText,
      renderCell: (row) => (
        <ReviewCell
          review={row.review}
          onChange={(value) => onChangeReview(row.ticketId, value)}
          participantName={row.clientInfo.FIO}
          modalTitle="Редактировать отзыв клиента"
          fieldLabel="Отзыв клиента"
        />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 32,
      cellClassName: styles.actionsCell,
      renderCell: (row) => <ClientMenuCell onDeleteClient={() => onDeleteClient(row.ticketId, row.clientInfo.FIO)} />,
    },
  ];
};
